/**
 * useViewport — minimal viewport-width signal shared by two callers:
 * the CMS authoring surface's desktop-only gate (FR-007 / SC-006) and
 * the member shell's switch to its desktop chrome (`Layout`). One value
 * serves both, so moving it for one moves the other.
 *
 * Return shape
 * ============
 *
 *   `{ isDesktop: boolean }` — a discrete predicate is easier to
 *   misuse-proof than a raw `width: number`. Callers that branch on
 *   "are we wide enough for the CMS editor?" read one boolean; callers
 *   that want raw pixels can read `window.innerWidth` themselves.
 *
 * Mechanism
 * =========
 *
 *   `window.matchMedia('(min-width: 1024px)')` is the canonical signal
 *   for a breakpoint match. We subscribe to its `'change'` events via
 *   `useSyncExternalStore` so React schedules a render on every
 *   transition across the breakpoint without polling the
 *   `window.innerWidth` value or re-subscribing per render.
 *
 *   The 1024 px breakpoint matches the project's tailwind/tokens
 *   `lg` boundary (the de-facto tablet → desktop divide). A future
 *   refactor that lifts the value to a token can replace
 *   `DESKTOP_MIN_WIDTH_PX` without touching the hook's body.
 *
 * SSR / non-DOM safety
 * ====================
 *
 *   The hook is SSR-safe: when `window` is undefined (Node, SSR
 *   pre-hydration) the snapshot path returns `isDesktop: true` so the
 *   CMS shell renders pessimistically (and the member shell mounts its
 *   desktop chrome) — gating the editor behind a
 *   "no window" probe would land server-rendered users on the
 *   desktop-required notice. apps/web is SPA-only today; this branch
 *   is defensive for future deferred-init paths.
 *
 *   `useSyncExternalStore`'s `getServerSnapshot` argument is wired so
 *   the static SSR snapshot collapses to the same constant without
 *   touching the (undefined) `window`.
 *
 * Cleanup
 * =======
 *
 *   `subscribe` returns a closure that removes the listener on
 *   unmount. `useSyncExternalStore` re-runs `subscribe` when its
 *   identity changes — both `subscribe` and `getSnapshot` are
 *   module-scope constants (no closure over component state), so they
 *   never re-subscribe.
 */

import { useSyncExternalStore } from 'react';

/**
 * Minimum viewport width that admits the CMS authoring surface and at
 * which the member shell swaps to its desktop chrome. The value mirrors
 * Tailwind's `lg` breakpoint (the project-wide tablet→desktop divide)
 * and aligns with FR-007 / SC-006's "desktop required" framing.
 */
export const DESKTOP_MIN_WIDTH_PX = 1024;

const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`;

// `useSyncExternalStore` requires stable `subscribe` / `getSnapshot`
// identities so it does not tear down and re-establish the
// subscription on every render. Module-scope functions are the
// simplest path: they never close over per-render state.

function subscribe(listener: () => void): () => void {
  // SSR / non-DOM environments fall through without subscribing.
  // `useSyncExternalStore` does not call the listener until subscribe
  // is set up, so the snapshot path is what determines the static
  // value.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {
      /* no-op: never subscribed */
    };
  }
  const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
  mql.addEventListener('change', listener);
  return () => {
    mql.removeEventListener('change', listener);
  };
}

function getSnapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // Match the SSR-snapshot value below — SPA + no `window` means
    // pessimistically claim "desktop" so the surface renders.
    return true;
  }
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

function getServerSnapshot(): boolean {
  // SSR path returns the same pessimistic value as the no-window
  // client path so hydration sees a stable result.
  return true;
}

export interface ViewportSnapshot {
  /** `true` iff the viewport meets the 1024 px desktop threshold. */
  readonly isDesktop: boolean;
}

/**
 * Read the viewport's desktop-vs-narrower predicate. React re-renders
 * the calling component on every transition across the breakpoint.
 */
export function useViewport(): ViewportSnapshot {
  const isDesktop = useSyncExternalStore<boolean>(subscribe, getSnapshot, getServerSnapshot);
  return { isDesktop };
}
